import { useEffect, useId, useMemo, type ReactElement } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { observer } from 'mobx-react-lite';

import { SubsetModel, type SubsetKey, type SubsetSource } from './SubsetModel';
import './EditSubset.scss';

export interface EditSubsetProps<T> {
  /** Names the control as a whole (the group's accessible name). */
  readonly label: string;
  readonly value: readonly T[];
  readonly onChange: (value: readonly T[]) => void;
  /** The universe to pick from. Pass a stable reference — see {@link SubsetSource}. */
  readonly source: SubsetSource<T>;
  /** Stable identity per item; used for ticking, removal and de-duplication. Never object identity. */
  readonly getKey: (item: T) => SubsetKey;
  /** The item's display text. Also what the filter matches, and what both panes sort on. */
  readonly getLabel: (item: T) => string;
  /** Plural noun for the empty states and the filter's accessible name, e.g. `"locks"`. */
  readonly itemNoun?: string;
  readonly disabled?: boolean;
}

interface OptionsProps<T> {
  readonly model: SubsetModel<T>;
  /** Prefix for the generated checkbox ids, so two controls on one page don't collide. */
  readonly idPrefix: string;
  readonly getKey: (item: T) => SubsetKey;
  readonly getLabel: (item: T) => string;
  readonly itemNoun: string;
  readonly onChange: (value: readonly T[]) => void;
}

/**
 * The left pane's scroll area: one checkbox per shown item, or the loading / error / empty note that
 * stands in for them. A component declared here rather than a closure built inside `EditSubset`, so
 * its identity is stable across renders instead of remounting the whole list on each one.
 *
 * `observer` because its observable reads happen in *its* render, not its parent's: an unobserved
 * child tracks nothing of its own and repaints only when the parent happens to. That the list
 * currently survives without it is an accident of `EditSubset` reading `filter` and `canAddAllShown`
 * (which derives from `shown`) — no test pins this, because none can while that overlap holds.
 */
const SubsetOptions = observer(<T,>({ model, idPrefix, getKey, getLabel, itemNoun, onChange }: OptionsProps<T>): ReactElement => {
  const { filter, isLoading, loadError, shown } = model;

  if (isLoading) {
    return (
      <div className="edit-subset__scroll">
        <div className="edit-subset__note">
          <Spinner animation="border" size="sm" role="status" /> Loading {itemNoun}…
        </div>
      </div>
    );
  }

  // A failed load is reported in place rather than as a toast: the control stays usable (the
  // selection comes from the value, not from the fetch), so this is state to read, not an event.
  if (loadError !== null) {
    return (
      <div className="edit-subset__scroll">
        <div className="edit-subset__note edit-subset__note--error">
          Could not load {itemNoun}: {loadError.message}
        </div>
      </div>
    );
  }

  if (shown.length === 0) {
    return (
      <div className="edit-subset__scroll">
        <div className="edit-subset__note">{filter.trim() === '' ? `No ${itemNoun} available` : `No ${itemNoun} match “${filter}”`}</div>
      </div>
    );
  }

  return (
    <div className="edit-subset__scroll">
      {shown.map((item, index) => (
        <Form.Check
          key={getKey(item)}
          // Indexed rather than keyed: an item key may hold characters an element id cannot.
          id={`${idPrefix}-option-${index}`}
          type="checkbox"
          className="edit-subset__option"
          label={getLabel(item)}
          checked={model.isSelected(item)}
          onChange={() => onChange(model.toggle(item))}
        />
      ))}
    </div>
  );
});

/**
 * Picks a subset of a known universe: a filterable checked list of every candidate beside the
 * current selection, listed the same way and removable a row at a time.
 *
 * The two panes overlap deliberately, because they answer different questions. The left answers
 * *"what is available, and is this one picked?"* — coverage, which can only be judged by seeing the
 * candidates you did **not** pick. The right answers *"what did I pick?"* without scrolling the
 * universe. A control that answers only one of the two is a checked list box or a chips input; this
 * is neither.
 *
 * **Not for thousands of rows** — a checked list over a universe too large to scroll is the wrong
 * control even with a filter. That case is `EditReference`, which is query-primary (type to reach an
 * item within a bounded page). See `../README.md`.
 *
 * The panes wrap to one column on **container** width (flex-wrap against each pane's flex-basis), so
 * the control also fits a narrow side panel on a wide screen, where a viewport breakpoint would not.
 *
 * All logic lives in {@link SubsetModel}; this is a pure `observer` over it. Note in particular that
 * the selection pane renders the *value*, not the loaded items filtered by it — see the model's
 * docblock for why that distinction is load-bearing.
 */
export const EditSubset = observer(
  <T,>({ label, value, onChange, source, getKey, getLabel, itemNoun = 'items', disabled = false }: EditSubsetProps<T>): ReactElement => {
    const id = useId();
    const model = useMemo(() => new SubsetModel(source, getKey, getLabel), [source, getKey, getLabel]);

    // The model mirrors the controlled value rather than owning it; without this the selection pane
    // stays empty and every tick is lost.
    useEffect(() => {
      model.syncValue(value);
    }, [model, value]);

    // Read what this render needs off the model here, so MobX tracks it in the observer that uses it.
    const { canAddAllShown, filter, selected } = model;

    return (
      <fieldset className="edit-subset" disabled={disabled}>
        {/* A legend, not the `Label` component: this names a group of controls, and `Label` renders a
            `<label>`, which belongs to exactly one. */}
        <legend className="edit-subset__title">{label}</legend>

        <div className="edit-subset__panes">
          <div className="edit-subset__pane">
            <div className="edit-subset__toolbar">
              <Form.Control
                type="search"
                size="sm"
                className="edit-subset__filter"
                value={filter}
                placeholder="Filter…"
                aria-label={`Filter ${itemNoun}`}
                onChange={(e) => model.setFilter(e.target.value)}
              />
              <Button variant="link" size="sm" disabled={!canAddAllShown} onClick={() => onChange(model.addAllShown())}>
                Add all shown
              </Button>
            </div>
            <SubsetOptions model={model} idPrefix={id} getKey={getKey} getLabel={getLabel} itemNoun={itemNoun} onChange={onChange} />
          </div>

          <div className="edit-subset__pane">
            <div className="edit-subset__toolbar">
              <span className="edit-subset__count">{selected.length} selected</span>
              <Button variant="link" size="sm" disabled={selected.length === 0} onClick={() => onChange(model.clear())}>
                Clear
              </Button>
            </div>
            <div className="edit-subset__scroll">
              {selected.length === 0 ? (
                <div className="edit-subset__note">Nothing selected</div>
              ) : (
                selected.map((item) => {
                  const orphan = model.isOrphan(item);
                  return (
                    <div
                      key={getKey(item)}
                      className={`edit-subset__selected${orphan ? ' edit-subset__selected--orphan' : ''}`}
                      title={orphan ? 'Selected, but not in the list — it may have been removed' : undefined}
                    >
                      <span className="edit-subset__selected-label">{getLabel(item)}</span>
                      {/* Only the icon removes: a whole-row target makes every click on the pane's text
                          destructive, and there is nothing else a row click could usefully mean. */}
                      <button
                        type="button"
                        className="edit-subset__remove"
                        aria-label={`Remove ${getLabel(item)}`}
                        onClick={() => onChange(model.toggle(item))}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </fieldset>
    );
  }
);
